'use client';

import React, { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { TrackedButton } from '@/components/button';
import { TRACKED_EVENT_POSITION } from '@/constants';
import { useNewsletterSubscribe } from '@/hooks/useNewsletterSubscribe.hook';

type NewsletterSubscribeProps = {
  namespace: 'Blog.newsletter' | 'News.newsletter';
  source?: string;
  /**
   * 구독 성공 시 호출. 넘기면 인라인 성공 메시지를 띄우지 않으므로
   * 호출자가 완료 모달 같은 성공 피드백을 대신 책임진다.
   */
  onSubscribed?: () => void;
};

/**
 * 본문 폭 전체를 쓰는 뉴스레터 구독 박스.
 *
 * 블로그 상세의 좌 레일 버전은 타이포 스케일이 완전히 달라(Figma Caption/500 ·
 * Button/Small/500) 이 컴포넌트와 공유하는 토큰이 없다. 그래서 한 컴포넌트에서
 * 분기하지 않고 `blog/[slug]/_components/rail-newsletter-subscribe.tsx` 가
 * 별도 마크업을 갖는다 — 폼 로직은 양쪽 모두 `useNewsletterSubscribe` 를 쓴다.
 */
function NewsletterSubscribe({
  namespace,
  source = 'newsletter_subscribe',
  onSubscribed,
}: NewsletterSubscribeProps) {
  const t = useTranslations(namespace);

  const { email, messageType, isLoading, handleEmailChange, handleSubmit } =
    useNewsletterSubscribe({ leadLabel: 'newsletter', source, onSubscribed });

  const isSuccess = messageType === 'fetch-success';
  const title = useMemo(() => t.rich('title', { br: () => <br /> }), [t]);
  const placeholder = t('placeholder');
  const buttonText = isLoading ? t('loading') : t('button');
  const messageClassName = useMemo(
    () =>
      `text-center text-caption ${isSuccess ? 'text-green-600' : 'text-red-600'}`,
    [isSuccess],
  );
  // onSubscribed 를 넘긴 경우 성공 messageType 은 훅이 세우지 않는다
  const message = messageType ? t(`message.${messageType}`) : null;

  return (
    <section className="flex w-full flex-col items-center gap-3 rounded-[20px] bg-3b-background-light px-5 py-8 md:gap-5 md:p-10">
      <h2 className="text-center text-sh1 font-semibold text-3b-text-primary md:text-h6">
        {title}
      </h2>
      <form
        onSubmit={handleSubmit}
        className="flex w-full flex-col items-center gap-3 md:gap-5"
      >
        <input
          type="email"
          value={email}
          onChange={handleEmailChange}
          placeholder={placeholder}
          aria-label={placeholder}
          aria-invalid={messageType === 'invalid-email' || undefined}
          aria-describedby={message ? 'newsletter-message' : undefined}
          autoComplete="email"
          className="w-full rounded-[10px] bg-white px-3.5 py-4 text-body1 text-3b-text-primary placeholder:text-3b-text-hint md:w-[333px]"
        />
        {/* label 은 배치와 무관하게 하나로 두고 position 으로 구분한다 — 목차
            (toc_item_n + right_sidebar/main_content)와 같은 방식 */}
        <TrackedButton
          label="newsletter_subscribe"
          position={TRACKED_EVENT_POSITION.MAIN_CONTENT}
          type="submit"
          disabled={isLoading}
          className="shadow-xs h-10 rounded-[30px] bg-3b-blue-light-900 px-5 py-2.5 text-[14px] font-semibold text-3b-text-contrast hover:bg-3b-blue-light-700 disabled:cursor-not-allowed disabled:opacity-50 md:h-auto md:w-[163px] md:rounded-[500px] md:text-[16px] md:shadow-none"
        >
          {buttonText}
        </TrackedButton>
      </form>
      {message ? (
        <p id="newsletter-message" role="alert" className={messageClassName}>
          {message}
        </p>
      ) : null}
    </section>
  );
}

export default NewsletterSubscribe;
